import media from '../media'
import fonts from '../fonts'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../components/store'
import { useAppDispatch, useAppSelector } from '../hooks'

function loadAsDataURL(url: string): Promise<string | null> {
    return new Promise((resolve) =>
        fetch(url)
            .then((response) => response.blob())
            .then((blob) => {
                var reader = new FileReader()
                reader.onload = function (e) {
                    resolve(e.target ? (e.target.result as string) : null)
                }
                reader.readAsDataURL(blob)
            }),
    )
}

export interface MediaState {
    loading: boolean
    initialized: boolean
    media: Record<keyof typeof media, string | null>
    fonts: Record<keyof typeof fonts, Record<string, string | null>>
    error: false | unknown
}
const initialState: MediaState = {
    loading: false,
    initialized: false,
    error: false,
    media: {},
    fonts: {},
} as MediaState
export const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    startHydration: (state) => {
      state.loading = true
    },
    mediaSuccessful: (state, action: PayloadAction<{ type: keyof typeof media, data: string }>) => {
      state.media[action.payload.type] = action.payload.data
    },
    fontSuccessful: (state, action: PayloadAction<{ font: keyof typeof fonts, type: string, data: string }>) => {
      if (!state.fonts[action.payload.font]) {
        state.fonts[action.payload.font] = {}
      }
      const font = state.fonts[action.payload.font]
      if (font) {
        font[action.payload.type] = action.payload.data
      }
    },
    finishHydration: (state) => {
      state.loading = false
      state.initialized = true
    },
    toggleBackgroundImage: (state) => {
      const currentImage = state.media['layout_bottom_withLogo']
      const nextImage = currentImage === media.layout_bottom_withLogo ? media.layout_bottom_withoutLogo : media.layout_bottom_withLogo
      state.media['layout_bottom_withLogo'] = nextImage
    },
    error: (state, action: PayloadAction<{ error: unknown }>) => {
      state.error = action.payload.error
      state.loading = false
    }
  },
})

// Action creators are generated for each case reducer function
//export const { hydrate } = formSlice.actions

export const selectInitialized = (state: RootState) => state.media.initialized
export const selectMedia = (state: RootState) => state.media.media
export const selectMediaItem = (item: keyof typeof media) => (state: RootState) => state.media.media[item]
export const selectFonts = (state: RootState) => state.media.fonts
export const { toggleBackgroundImage } = mediaSlice.actions;

export default mediaSlice.reducer

export function hydrate() {
  return async (dispatch: ReturnType<typeof useAppDispatch>) => {
    const { loading, initialized } = useAppSelector((state: RootState) => state.media)
    if (loading || initialized) return;

    dispatch(mediaSlice.actions.startHydration())

    try {
      await Promise.all([
        ...Object.entries(media).map(([item, url]) => loadAsDataURL(url).then((data) => data ? dispatch(mediaSlice.actions.mediaSuccessful({ type: item as keyof typeof media, data })) : undefined)),
        ...Object.entries(fonts).map(([font, types]) => Promise.all(Object.entries(types).map(([type, url]) => loadAsDataURL(url).then((data) => data ? dispatch(mediaSlice.actions.fontSuccessful({ font: font as keyof typeof fonts, type, data })) : undefined)))),
      ])
      dispatch(mediaSlice.actions.finishHydration())
    } catch (error) {
      dispatch(mediaSlice.actions.error({ error }))
    }
  }
}
